<template>
	<article v-if="data" class="article-content">
		<header class="article-content__header">
			<img
				src="../../../images/back-tuwen.png"
				alt=""
				class="article-content__back"
				@click="goBack"
			/>
			<div class="article-content__publisher">
				<img
					:src="publisherInfo.avatar"
					:alt="publisherInfo.nickname"
					class="article-content__publisher-avatar"
				/>
				<span class="article-content__publisher-name">{{ publisherInfo.nickname }}</span>
			</div>
			<div class="article-content__header-actions">
				<button
					type="button"
					class="article-content__follow-btn"
					:class="{ 'article-content__follow-btn--followed': isFollowed }"
					@click="handleFollow"
				>
					{{ isFollowed ? t("square.detail.followed") : t("square.detail.follow") }}
				</button>
				<button type="button" class="article-content__share-btn" @click="showSharePanel = true">
					<img :src="shareIcon" alt="" />
				</button>
			</div>
		</header>

		<figure v-if="displayImages.length" class="article-content__media">
			<swiper
				v-if="displayImages.length > 1"
				:modules="[Pagination]"
				:auto-height="true"
				:pagination="{ clickable: true }"
			>
				<swiper-slide v-for="(item, index) in displayImages" :key="`${item}-${index}`">
					<img
						:src="item"
						:alt="t('square.detail.article.coverAlt', { title: data.title })"
						class="article-content__image"
					/>
				</swiper-slide>
			</swiper>
			<img
				v-else
				:src="displayImages[0]"
				:alt="t('square.detail.article.coverAlt', { title: data.title })"
				class="article-content__image"
			/>
		</figure>

		<div class="article-content__body">
			<h1 v-if="data.title" class="article-content__title">{{ data.title }}</h1>
			<div
				v-if="articleContent"
				class="article-content__article-body"
				v-html="articleContent"
			></div>
		</div>

		<TeacherCard :square-info="data" :square-id="squareId" />

		<section ref="commentsRef" class="article-content__comments">
			<div class="article-content__tabs">
				<button
					type="button"
					class="article-content__tab"
					:class="{ 'article-content__tab--active': commentTab === 'hot' }"
					@click="commentTab = 'hot'"
				>
					{{ t("square.detail.shorts.tabHot") }}
				</button>
				<button
					type="button"
					class="article-content__tab"
					:class="{ 'article-content__tab--active': commentTab === 'new' }"
					@click="commentTab = 'new'"
				>
					{{ t("square.detail.shorts.tabNew") }}
				</button>
			</div>

			<ul v-if="commentList.length" class="article-content__comment-list">
				<li v-for="item in commentList" :key="item.id" class="article-content__comment-item">
					<img
						:src="item.user_info?.avatar"
						:alt="item.user_info?.nickname"
						class="article-content__comment-avatar"
					/>
					<div class="article-content__comment-content">
						<div class="article-content__comment-head">
							<span class="article-content__comment-user">@{{ item.user_info?.nickname }}</span>
							<span class="article-content__comment-time">·{{ item.format_time }}</span>
							<span v-if="isAuthorComment(item)" class="article-content__author-tag">
								{{ t("square.detail.authorTag") }}
							</span>
						</div>
						<p class="article-content__comment-text">{{ item.reply }}</p>
						<div class="article-content__comment-actions">
							<button
								type="button"
								class="article-content__comment-zan"
								:disabled="isCommentZanLoading(item.id)"
								@click.stop="handleCommentZan(item)"
							>
								<img :src="item.is_zan ? zanAfterIcon : zanBeforeIcon" alt="" />
								<span>{{ item.zan || 0 }}</span>
							</button>
							<button
								type="button"
								class="article-content__comment-reply"
								@click.stop="toggleReplyInput(item)"
							>
								<img :src="pinlunIcon" alt="" />
							</button>
						</div>
						<div
							v-if="String(activeReplyId) === String(item.id)"
							class="article-content__reply-input"
						>
							<input
								v-model="activeReplyDraft"
								type="text"
								:placeholder="
									t('square.detail.replyPlaceholder', {
										nickname: item.user_info?.nickname || '',
									})
								"
								maxlength="500"
								:disabled="replySubmitting"
								@keyup.enter="submitReplyComment(item)"
							/>
							<button
								type="button"
								class="article-content__reply-send"
								:disabled="replySubmitting || !activeReplyDraft.trim()"
								@click="submitReplyComment(item)"
							>
								{{ replySubmitting ? t("square.detail.sending") : t("square.detail.send") }}
							</button>
						</div>
						<button
							v-if="item.reply_count > 0"
							type="button"
							class="article-content__view-replies"
							:disabled="getReplyState(item.id).loading"
							@click="toggleViewReplies(item)"
						>
							<template v-if="getReplyState(item.id).loading">{{
								t("square.detail.loading")
							}}</template>
							<template v-else-if="getReplyState(item.id).expanded">{{
								t("square.detail.collapseReplies")
							}}</template>
							<template v-else
								>{{ t("square.detail.viewReplies", { count: item.reply_count }) }} &gt;</template
							>
						</button>
						<ul
							v-if="getReplyState(item.id).expanded && getReplyState(item.id).list.length"
							class="article-content__reply-list"
						>
							<li
								v-for="reply in getReplyState(item.id).list"
								:key="reply.id"
								class="article-content__reply-item"
							>
								<img
									:src="reply.user_info?.avatar"
									:alt="reply.user_info?.nickname"
									class="article-content__reply-avatar"
								/>
								<div class="article-content__reply-body">
									<div class="article-content__comment-head">
										<span class="article-content__comment-user"
											>@{{ reply.user_info?.nickname }}</span
										>
										<span class="article-content__comment-time">·{{ reply.format_time }}</span>
									</div>
									<p class="article-content__comment-text">{{ reply.reply }}</p>
									<button
										type="button"
										class="article-content__comment-zan"
										:disabled="isCommentZanLoading(reply.id)"
										@click.stop="handleCommentZan(reply)"
									>
										<img :src="reply.is_zan ? zanAfterIcon : zanBeforeIcon" alt="" />
										<span>{{ reply.zan || 0 }}</span>
									</button>
								</div>
							</li>
							<li v-if="hasMoreReplies(item.id)" class="article-content__load-more-wrap">
								<button
									type="button"
									class="article-content__view-replies"
									:disabled="getReplyState(item.id).loading"
									@click="loadMoreReplies(item)"
								>
									{{
										getReplyState(item.id).loading
											? t("square.detail.loading")
											: t("square.detail.loadMoreReplies")
									}}
								</button>
							</li>
						</ul>
					</div>
				</li>
			</ul>
			<p v-else-if="!commentLoading" class="article-content__empty">
				{{ t("square.detail.emptyComments") }}
			</p>
			<button
				v-if="hasMoreComments"
				type="button"
				class="article-content__load-more"
				:disabled="commentLoading"
				@click="loadMoreComments"
			>
				{{ commentLoading ? t("square.detail.loading") : t("square.detail.loadMoreComments") }}
			</button>
		</section>

		<div class="article-content__bottom-spacer" aria-hidden="true"></div>

		<div class="article-content__bottom-bar" :class="{ 'is-input-focused': isCommentInputFocused }">
			<div class="article-content__input-wrap">
				<img :src="writeIcon" alt="" class="article-content__write-icon" aria-hidden="true" />
				<input
					v-model="mainCommentText"
					type="text"
					:placeholder="t('square.detail.commentPlaceholder')"
					maxlength="500"
					:disabled="commentSubmitting"
					@focus="onCommentInputFocus"
					@blur="onCommentInputBlur"
					@keyup.enter="submitMainComment"
				/>
				<button
					type="button"
					class="article-content__send-btn"
					:disabled="commentSubmitting || !mainCommentText.trim()"
					@click="submitMainComment"
				>
					<img :src="mainCommentText.trim() ? submit2 : submit1" alt="" />
				</button>
			</div>
			<button
				v-show="!isCommentInputFocused"
				type="button"
				class="article-content__bottom-action"
				:disabled="squareZanLoading"
				@click="handleZan"
			>
				<img :src="isZaned ? zanAfterIcon : zanBeforeIcon" alt="" />
				<span>{{ t("square.detail.like") }}</span>
			</button>
			<button
				v-show="!isCommentInputFocused"
				type="button"
				class="article-content__bottom-action"
				@click="scrollToComments"
			>
				<img :src="pinlunIcon" alt="" />
				<span>{{ t("square.detail.comments") }}</span>
			</button>
		</div>

		<SharePanel v-model:show="showSharePanel" :share-list="shareList" @share="handleShare" />
	</article>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination } from "swiper";
import { useI18n } from "~/composables/useI18n";
import { useSquare } from "~/composables/useSquare";
import { safeNavigateBack } from "~/utils/safeNavigateBack";
import TeacherCard from "../teacher-card/index.vue";
import SharePanel from "../share-panel/index.vue";
import {
	buildDetailContentParams,
	resolveDetailMediaType,
	useArticleReadProgress,
} from "../../../composables/useSquareDetailTracking";
import shareIcon from "../../../images/sahre.png";
import zanAfterIcon from "../../../images/zan-after.png";
import zanBeforeIcon from "../../../images/zan-before-m.png";
import pinlunIcon from "../../../images/pinlun-m.png";
import writeIcon from "../../../images/write.svg";
import submit1 from "../../../images/submit1.png";
import submit2 from "../../../images/submit2.png";
import "swiper/css";
import "swiper/css/pagination";

const props = defineProps({
	data: {
		type: Object,
		default: () => ({}),
	},
	squareId: {
		type: String,
		default: "",
	},
});

const { t, locale } = useI18n();
const logDetailEvent = inject("logDetailEvent", null);

const commentTab = ref("hot");
const commentsRef = ref(null);
const scrollContainerRef = ref(null);

const { handleScroll: handleArticleScroll } = useArticleReadProgress(
	logDetailEvent,
	() => props.data,
	scrollContainerRef
);

let removeArticleScrollListener = null;
const isCommentInputFocused = ref(false);
let commentInputBlurTimer = null;

onMounted(() => {
	if (!import.meta.client) return;
	const container = document.querySelector(".mobile-layout") || document.documentElement;
	scrollContainerRef.value = container;
	const onScroll = () => handleArticleScroll();
	container.addEventListener("scroll", onScroll, { passive: true });
	removeArticleScrollListener = () => container.removeEventListener("scroll", onScroll);
});

onBeforeUnmount(() => {
	removeArticleScrollListener?.();
	if (commentInputBlurTimer) {
		clearTimeout(commentInputBlurTimer);
		commentInputBlurTimer = null;
	}
});

const onCommentInputFocus = () => {
	if (commentInputBlurTimer) {
		clearTimeout(commentInputBlurTimer);
		commentInputBlurTimer = null;
	}
	isCommentInputFocused.value = true;
	if (!logDetailEvent) return;
	logDetailEvent(
		"media_comment_input_focus",
		buildDetailContentParams(props.data, {
			media_type: resolveDetailMediaType(props.data),
		})
	);
};

const onCommentInputBlur = () => {
	commentInputBlurTimer = setTimeout(() => {
		isCommentInputFocused.value = false;
		commentInputBlurTimer = null;
	}, 150);
};

const {
	isFollowed,
	isZaned,
	squareZanLoading,
	shareList,
	showSharePanel,
	handleFollow,
	handleZan,
	handleShare,
	commentList,
	commentLoading,
	commentTotal,
	hasMoreComments,
	mainCommentText,
	activeReplyId,
	activeReplyDraft,
	commentSubmitting,
	replySubmitting,
	getReplyState,
	hasMoreReplies,
	toggleViewReplies,
	loadMoreReplies,
	loadMoreComments,
	toggleReplyInput,
	submitMainComment,
	submitReplyComment,
	handleCommentZan,
	isCommentZanLoading,
} = useSquare(
	() => props.data,
	() => commentTab.value
);

const publisherInfo = computed(() => {
	return (
		props.data?.publisher_info || {
			nickname: "",
			avatar: "",
		}
	);
});

const authorUid = computed(() => props.data?.uid || props.data?.publisher_info?.uid);

const isAuthorComment = comment => {
	if (!authorUid.value || !comment?.user_info?.uid) return false;
	return String(comment.user_info.uid) === String(authorUid.value);
};

const mediaImages = computed(() => {
	const mediaUrl = props.data?.media_url;
	if (!mediaUrl) return [];
	if (Array.isArray(mediaUrl)) {
		return mediaUrl.filter(item => typeof item === "string" && item.trim());
	}
	if (typeof mediaUrl === "string") {
		try {
			const parsed = JSON.parse(mediaUrl);
			if (Array.isArray(parsed)) {
				return parsed
					.map(item =>
						String(item || "")
							.replace(/\\\//g, "/")
							.trim()
					)
					.filter(Boolean);
			}
		} catch {
			return mediaUrl
				.split(",")
				.map(item =>
					item
						.replace(/[\[\]"]/g, "")
						.replace(/\\\//g, "/")
						.trim()
				)
				.filter(Boolean);
		}
	}
	return [];
});

const displayImages = computed(() => {
	if (mediaImages.value.length) return mediaImages.value;
	const cover = props.data?.cover;
	return cover ? [cover] : [];
});

const articleContent = computed(() => {
	if (!props.data) return "";
	if (locale.value === "en") {
		return props.data.content_en || props.data.content || "";
	}
	return props.data.content || props.data.content_en || "";
});

const goBack = () => {
	safeNavigateBack("/seer/square/article");
};

const scrollToComments = () => {
	if (!import.meta.client) return;
	const target = commentsRef.value;
	if (!target) return;

	const mobileLayout = document.querySelector(".mobile-layout");
	if (mobileLayout) {
		const top = target.getBoundingClientRect().top + mobileLayout.scrollTop - 88;
		mobileLayout.scrollTo({ top, behavior: "smooth" });
		return;
	}

	target.scrollIntoView({ behavior: "smooth", block: "start" });
};
</script>

<style lang="scss" scoped>
.article-content {
	width: 750px;
	background: #ffffff;
	padding-bottom: 0;

	&__header {
		position: sticky;
		top: 0;
		z-index: 10;
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 16px 24px;
		background: #ffffff;
		border-bottom: 1px solid #f0f0f0;
	}

	&__back {
		width: 48px;
		height: 48px;
		flex-shrink: 0;
		cursor: pointer;
	}

	&__publisher {
		flex: 1;
		min-width: 0;
		display: flex;
		align-items: center;
		gap: 12px;
	}

	&__publisher-avatar {
		width: 64px;
		height: 64px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
	}

	&__publisher-name {
		font-size: 28px;
		font-weight: 500;
		color: #333333;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__header-actions {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-shrink: 0;
	}

	&__follow-btn {
		padding: 8px 20px;
		border: none;
		border-radius: 8px;
		background: #f2af19;
		color: #ffffff;
		font-size: 24px;
		font-weight: 500;
		cursor: pointer;
		white-space: nowrap;

		&--followed {
			background: #f0f0f0;
			color: #999999;
		}
	}

	&__share-btn {
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;

		img {
			width: 48px;
			height: 48px;
			object-fit: contain;
		}
	}

	&__media {
		margin: 0;
		line-height: 0;

		:deep(.swiper-slide) {
			display: flex;
			justify-content: center;
			align-items: center;
		}

		:deep(.swiper-pagination) {
			bottom: 16px;
		}

		:deep(.swiper-pagination-bullet) {
			width: 12px;
			height: 12px;
			background: rgba(0, 0, 0, 0.2);
			opacity: 1;
		}

		:deep(.swiper-pagination-bullet-active) {
			width: 28px;
			border-radius: 6px;
			background: #333333;
		}
	}

	&__image {
		width: 100%;
		height: auto;
		display: block;
		object-fit: contain;
	}

	&__body {
		padding: 28px 30px 0;
	}

	&__title {
		margin: 0 0 20px;
		font-size: 32px;
		font-weight: 700;
		color: #333333;
		line-height: 1.5;
	}

	&__article-body {
		font-size: 28px;
		color: #333333;
		line-height: 1.8;
		word-break: break-word;

		:deep(p) {
			margin: 0 0 20px;

			&:last-child {
				margin-bottom: 0;
			}
		}

		:deep(img) {
			max-width: 100%;
			height: auto;
			border-radius: 8px;
		}
	}

	&__comments {
		padding: 8px 30px 0;
	}

	&__tabs {
		display: flex;
		gap: 40px;
		border-bottom: 1px solid #f0f0f0;
		margin-bottom: 8px;
	}

	&__tab {
		border: none;
		background: transparent;
		padding: 20px 0 16px;
		font-size: 28px;
		color: #a0a0a0;
		cursor: pointer;
		position: relative;

		&--active {
			color: #333333;
			font-weight: 600;

			&::after {
				content: "";
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 4px;
				background: #1f2329;
				border-radius: 2px;
			}
		}
	}

	&__comment-list {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	&__comment-item {
		display: flex;
		gap: 20px;
		padding: 28px 0;
		border-bottom: 1px solid #f0f0f0;

		&:last-child {
			border-bottom: none;
		}
	}

	&__comment-avatar {
		width: 64px;
		height: 64px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
		background: #eeeeee;
	}

	&__comment-content {
		flex: 1;
		min-width: 0;
	}

	&__comment-head {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 4px;
		margin-bottom: 10px;
	}

	&__comment-user,
	&__comment-time {
		font-size: 24px;
		color: #888888;
	}

	&__author-tag {
		padding: 2px 10px;
		border-radius: 4px;
		background: #1f2329;
		color: #ffffff;
		font-size: 20px;
		line-height: 1.4;
	}

	&__comment-text {
		margin: 0 0 16px;
		font-size: 28px;
		color: #1f2329;
		line-height: 1.6;
		word-break: break-word;
	}

	&__comment-actions {
		display: flex;
		align-items: center;
		gap: 32px;
	}

	&__comment-zan,
	&__comment-reply {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		font-size: 24px;
		color: #666666;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		img {
			width: 32px;
			height: 32px;
			object-fit: contain;
		}
	}

	&__reply-input {
		display: flex;
		align-items: center;
		gap: 16px;
		margin-top: 16px;

		input {
			flex: 1;
			min-width: 0;
			height: 64px;
			padding: 0 24px;
			border: none;
			border-radius: 32px;
			background: #f5f5f5;
			font-size: 26px;
			outline: none;
		}
	}

	&__reply-send {
		flex-shrink: 0;
		height: 56px;
		padding: 0 24px;
		border: none;
		border-radius: 28px;
		background: #f2af19;
		color: #ffffff;
		font-size: 24px;
		cursor: pointer;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	&__view-replies {
		border: none;
		background: transparent;
		padding: 0;
		margin-top: 12px;
		font-size: 24px;
		color: #999999;
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
		}
	}

	&__reply-list {
		list-style: none;
		margin: 16px 0 0;
		padding: 0 0 0 20px;
		border-left: 2px solid #f0f0f0;
	}

	&__reply-item {
		display: flex;
		gap: 16px;
		margin-bottom: 24px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	&__reply-avatar {
		width: 48px;
		height: 48px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
	}

	&__reply-body {
		flex: 1;
		min-width: 0;
	}

	&__load-more-wrap {
		list-style: none;
		margin-top: 12px;
	}

	&__empty {
		margin: 0;
		padding: 60px 0;
		text-align: center;
		font-size: 28px;
		color: #999999;
	}

	&__load-more {
		display: block;
		width: 100%;
		margin: 16px 0 24px;
		padding: 16px 0;
		border: none;
		background: transparent;
		font-size: 26px;
		color: #666666;
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
			color: #bbbbbb;
		}
	}

	&__bottom-spacer {
		height: 140px;
	}

	&__bottom-bar {
		position: fixed;
		left: 50%;
		bottom: 0;
		transform: translateX(-50%);
		z-index: 20;
		width: 750px;
		display: flex;
		align-items: center;
		gap: 20px;
		padding: 16px 24px calc(16px + env(safe-area-inset-bottom));
		background: #ffffff;
		border-top: 1px solid #f0f0f0;
		box-sizing: border-box;

		&.is-input-focused {
			.article-content__input-wrap {
				flex: 1 1 100%;
				width: 100%;
			}
		}
	}

	&__input-wrap {
		flex: 1;
		min-width: 0;
		display: flex;
		align-items: center;
		gap: 12px;
		height: 72px;
		padding: 0 12px 0 20px;
		background: #f5f5f5;
		border-radius: 36px;

		input {
			flex: 1;
			min-width: 0;
			height: 100%;
			border: none;
			background: transparent;
			font-size: 28px;
			color: #333333;
			outline: none;

			&::placeholder {
				color: #bbbbbb;
			}

			&:disabled {
				cursor: not-allowed;
			}
		}
	}

	&__write-icon {
		width: 28px;
		height: 28px;
		object-fit: contain;
		flex-shrink: 0;
	}

	&__send-btn {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.6;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	&__bottom-action {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		gap: 4px;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		min-width: 56px;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		img {
			width: 64px;
			height: 64px;
			object-fit: contain;
		}

		span {
			font-size: 24px;
			color: #333333;
			line-height: 1;
		}
	}
}
</style>
